import { useEffect, useState } from "react";
import { g, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { TrendingUp, Banknote, Percent, Users, Bike } from "lucide-react";
import { PrimiPassi } from "@/components/PrimiPassi";
import { Novita } from "@/components/Novita";
import { AccessiRapidi } from "@/components/AccessiRapidi";
import { ChiCeStasera } from "@/components/ChiCeStasera";
import { SituazioneCassa } from "@/components/SituazioneCassa";
import { ChiusuraSerata } from "@/components/ChiusuraSerata";

const TakeawayCard = () => {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => {
    g("/takeaway/summary").then(setD);
  }, []);
  if (!d) return null;
  return (
    <Card className="p-6" data-testid="serata-takeaway">
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <Bike className="h-4 w-4" /> {t("sr.takeaway")}
      </h3>
      {d.orders === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t("sr.noTakeaway")}</p>
      ) : (
        <div className="mt-4 space-y-1 text-sm">
          <div className="flex justify-between text-slate-500">
            <span>{t("sr.orders")}</span>
            <span className="font-num">{d.orders}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>{t("sr.gross")}</span>
            <span className="font-num">{eur(d.gross)}</span>
          </div>
          <div className="flex justify-between text-rose-600">
            <span>{t("sr.fees")}</span>
            <span className="font-num">-{eur(d.fee)}</span>
          </div>
          <div className="flex items-baseline justify-between border-t border-slate-100 pt-2 font-semibold">
            <span>{t("sr.net")}</span>
            <span className="font-num text-xl">{eur(d.net)}</span>
          </div>
          <div className="mt-3 space-y-1">
            {d.per_channel.map((c) => (
              <div key={c.channel} className="flex justify-between text-xs text-slate-500">
                <span>{c.channel} · {t("sr.chOrders", { n: c.orders })}</span>
                <span className="font-num">{t("sr.chNet", { v: eur(c.net) })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default function Serata() {
  const { t } = useI18n();
  const { demoMode } = useApp();
  const [d, setD] = useState(null);
  const load = () => g("/evening").then(setD);
  useEffect(() => {
    load();
  }, []);

  if (!d) return <p className="text-sm text-slate-500">{t("g.loading")}</p>;
  const maxH = Math.max(1, ...d.hourly.map((h) => h.total));
  const maxD = Math.max(1, ...d.top_dishes.map((x) => x.qty));

  return (
    <div className="fade-up space-y-6" data-testid="serata-page">
      {/* Nella demo la dashboard resta pulita: chi guarda deve vedere il lavoro, non gli avvisi */}
      {!demoMode && (
        <>
          <PrimiPassi />
          <Novita />
        </>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("g.owner")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("sr.title")}</h1>
          <p className="text-xs text-slate-500">{t("sr.window")}</p>
        </div>
        <ChiusuraSerata onDone={load} />
      </div>

      <SituazioneCassa d={d} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["sold", t("sr.sold"), eur(d.sold), t("sr.closedTables", { n: d.closed_tables }), TrendingUp],
          ["cashed", t("sr.cashed"), eur(d.cashed),
            d.compare?.pct === null || d.compare?.weeks === 0
              ? t("sr.avgTicket", { v: eur(d.avg_ticket) })
              : t("sr.vsPast", { p: `${d.compare.pct > 0 ? "+" : ""}${d.compare.pct}`, n: d.compare.weeks }),
            Banknote],
          ["discounts", t("sr.discounts"), eur(d.discounts), t("sr.covers", { n: d.covers }), Percent],
          ["tables", t("sr.openTables"), d.open_tables, t("sr.waiters", { n: d.per_waiter.length }), Users],
        ].map(([id, l, v, s, I]) => (
          <Card key={id} className="p-6" data-testid={`serata-kpi-${id}`}>
            <div className="flex items-start justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{l}</p>
              <I className="h-4 w-4 text-slate-400" />
            </div>
            <p className="font-num mt-4 text-3xl font-bold">{v}</p>
            <p className="mt-1 text-xs text-slate-500">{s}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2" data-testid="serata-hourly">
          <h3 className="text-lg font-semibold">{t("sr.hourly")}</h3>
          {d.hourly.length === 0 ? (
            <p className="mt-6 text-sm text-slate-500">{t("sr.noClosed")}</p>
          ) : (
            <div className="mt-6 flex h-48 items-end gap-3">
              {d.hourly.map((h) => (
                <div key={h.hour} style={{ maxWidth: 90 }} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                  <span className="font-num text-[11px] text-slate-500">{eur(h.total)}</span>
                  <div className="w-full rounded-t bg-slate-900" style={{ height: `${(h.total / maxH) * 100}%`, minHeight: 6 }} />
                  <span className="font-num text-[11px] text-slate-500">{h.hour}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="serata-top-dishes">
          <h3 className="text-lg font-semibold">{t("sr.topDishes")}</h3>
          <ul className="mt-5 space-y-3">
            {d.top_dishes.map((x) => (
              <li key={x.name}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium">{x.name}</span>
                  <span className="font-num text-sm text-slate-500">{x.qty}</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(x.qty / maxD) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="p-6" data-testid="serata-per-waiter">
        <h3 className="text-lg font-semibold">{t("sr.perWaiter")}</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <th className="py-2 font-bold">{t("sr.waiter")}</th>
                <th className="py-2 font-bold">{t("sr.tables")}</th>
                <th className="py-2 font-bold">{t("st.covers")}</th>
                <th className="py-2 text-right font-bold">{t("sr.total")}</th>
              </tr>
            </thead>
            <tbody>
              {d.per_waiter.map((w) => (
                <tr key={w.waiter} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 font-medium">{w.waiter}</td>
                  <td className="py-3 font-num">{w.tables}</td>
                  <td className="py-3 font-num">{w.covers}</td>
                  <td className="py-3 text-right font-num font-semibold">{eur(w.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <TakeawayCard />

      <AccessiRapidi />
      <ChiCeStasera />
    </div>
  );
}
